import { intercomService } from "@services/intercomService";
import { queriesInvoiceRows, queriesInvoices, queriesPayers } from "@query";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import {
  copyText,
  toWSDateString,
  useWSSnackbar,
  WSButton,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { ICollaboratorSchema } from "@wingspanhq/payments/dist/interfaces";
import {
  Invoice,
  InvoiceStatus,
  PayoutDestinationSummary,
  PayoutDestinationSummaryType
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { Card } from "../../modules/PaymentApp/components/Card/Card";
import { DetailsLayout } from "../../components/DetailsLayout/DetailsLayout";
import { UrlIdKey } from "../../constants/routing";
import { AlertEnableTaxWithholdings } from "../../modules/TaxWithholdings/components/AlertEnableTaxWithholdings";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import {
  useMutationDownloadInvoiceReceiptPdf,
  useMutationSendInvoice
} from "../../query/payments/mutations";
import { WSQueries } from "../../query/WSQuery";
import { selectorIsDomesticMember } from "../../shared/selectors/selectorIsDomesticMember";
import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";
import { selectorIsInvoicePaidOffPlatform } from "../selectors/selectorIsInvoicePaidOffPlatform";
import { useSetWSStore } from "../../store";
import { InvoiceActivity } from "../components/InvoiceActivity";
import { InvoiceDetails } from "../components/InvoiceDetails";
import { InvoiceDetailsHeader } from "../components/InvoiceDetailsHeader/InvoiceDetailsHeader";
import { InvoicePayments } from "../components/InvoicePayments";
import { InvoiceRecurringSeriesCard } from "../components/InvoiceRecurringSeriesCard/InvoiceRecurringSeriesCard";
import { InvoiceRefunds } from "../components/InvoiceRefunds";
import { QuickbooksHistory } from "../components/QuickbooksHistory/QuickbooksHistory";
import { QuickbooksWrapper } from "../components/QuickbooksWrapper/QuickbooksWrapper";
import { INVOICE_EXPAND } from "../constants";

const payoutDestinationLabel = (
  destination?: PayoutDestinationSummary
): string => {
  const suffix = destination?.last4 ? ` (${destination.last4})` : "";
  switch (destination?.type) {
    case PayoutDestinationSummaryType.INTERNAL_ACCOUNT:
      return "Wingspan Wallet";
    case PayoutDestinationSummaryType.TAX_WITHHOLDING:
      return "Tax Withholding";
    case PayoutDestinationSummaryType.EXTERNAL_BANK_ACCOUNT:
      return `${destination.brand || "Bank account"}${suffix}`;
    case PayoutDestinationSummaryType.PAYMENT_CARD:
      return `${destination.brand || "Debit"}${suffix}`;
    case PayoutDestinationSummaryType.CHECK:
      return "Check";
    case PayoutDestinationSummaryType.GIFT_CARD:
      return "Gift card";
    default:
      return "";
  }
};

const getInvoicePayoutSummaryV3 = (invoice: Invoice): string => {
  const descriptions = (invoice.payouts || [])
    .map(payout => payoutDestinationLabel(payout.destination))
    .filter(Boolean);

  if (descriptions.length === 0) {
    return "Funds have been deposited";
  }

  return `Funds deposited to ${descriptions.join(", ")}`;
};

const getIsInstantPayoutAllowedV3 = (
  invoice: Invoice,
  internationalIP?: boolean
) =>
  invoice.status === InvoiceStatus.PAID &&
  !selectorIsInvoicePaidOffPlatform(invoice) &&
  !invoice.events?.depositedAt;
// TODO(v3): should check user's country
// (selectorIsDomesticMember(member) ||
//   (!!internationalIP && member.profile.address?.country === "CA"));

type Props = RouteComponentProps<
  {
    invoiceId: string;
    year: string;
    listName?: string;
  },
  {},
  { backPath?: string }
>;

export const RouteInvoiceDetails: React.FC<Props> = ({
  match,
  location,
  history
}) => {
  const invoiceId = match.params.invoiceId;

  const queryInvoice = queriesInvoices.getInvoice.useQuery({
    invoiceId,
    expand: INVOICE_EXPAND
  });
  // The `Payer` read stays for the contact email and the v1-shaped payer the
  // legacy sections below take; the displayed name comes off the search row's
  // server-resolved `profile.resolvedName`, so it matches the name the invoice
  // tables show for this client.
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: queryInvoice.data?.payerId! },
    {
      enabled: !!queryInvoice.data?.payerId,
      retry: false
    }
  );
  // The client's name comes off this invoice's own search row, which carries the
  // counterparty profile the invoices table renders — so the drawer and the list
  // it was opened from agree by construction, with no second counterparty read.
  // Deliberately outside the blocking query set below: a missing row costs the
  // name, not the page (ARC-990).
  const queryInvoiceRow = queriesInvoiceRows.searchInvoiceRows.useQuery(
    { filter: { id: { eq: invoiceId } }, page: { size: 1 } },
    { enabled: !!invoiceId }
  );
  const queryFeatureFlags = useQueryFeatureFlags();

  const year = parseInt(
    match.params[UrlIdKey.Year]
  ) as keyof ICollaboratorSchema["form1099Balances"];
  const is1099Flow = !isNaN(year);

  const { openSnackbar } = useWSSnackbar();
  const [createInvoiceSecureLink, createInvoiceSecureLinkMeta] =
    queriesInvoices.createInvoiceSecureLink.useMutation();
  const copyInvoiceLink = () => {
    createInvoiceSecureLink(
      { invoiceId },
      {
        onSuccess: link => {
          if (link?.url) {
            copyText(link.url);
            openSnackbar({ type: "success", message: "Invoice link copied" });
          }
        }
      }
    );
  };
  const [sendInvoice, sendInvoiceMeta] = useMutationSendInvoice();
  const [sendInvoiceReminder, sendInvoiceReminderMeta] =
    queriesInvoices.remindInvoice.useMutation({
      onSuccess: () => {
        openSnackbar({ type: "success", message: "Reminder sent" });
      }
    });
  const [downloadInvoiceReceiptPdf, downloadInvoiceReceiptPdfMeta] =
    useMutationDownloadInvoiceReceiptPdf();

  const setStore = useSetWSStore();

  return (
    <DetailsLayout
      onBack={() => {
        history.push({
          pathname: location.state?.backPath
            ? location.state.backPath
            : `/member/invoices${
                match.params.listName ? `/${match.params.listName}` : ""
              }`,
          search: location.search
        });
      }}
      title={
        queryInvoice.isLoading
          ? ""
          : `Invoice #${queryInvoice.data?.invoiceNumber || " Pending"}`
      }
    >
      <WSQueries
        queries={{
          queryInvoice,
          queryPayer,
          queryFeatureFlags
        }}
      >
        {({
          queryInvoice: { data: invoice },
          queryPayer: { data: payer },
          queryFeatureFlags: { data: featureFlags }
        }) => {
          const invoiceRow = queryInvoiceRow.data?.data?.[0];
          const payerName = invoiceRow ? selectorSearchRowName(invoiceRow) : "";
          // `Payer.email` is the only trustworthy recipient address here. The
          // invoice search row also carries an `email`, and by schema it is the
          // payer's — but when the payer has no linked account the projection
          // fills it with `wsv3-<viewer's own accountId>@noreply.wingspan.app`,
          // the same synthetic string for every client. Falling back to it
          // would name a non-deliverable machine address as the recipient,
          // which is worse than naming none.
          //
          // So when the address is missing the sentence names the client rather
          // than trailing off after "Sent to", as RouteInvoiceConfirmation does.
          const recipient = payer.email || "your client";

          const editButtonProps = {
            children: "Edit",
            onClick: () => {
              setStore({ invoiceFormBackPath: location.pathname });
              history.push(`/member/invoices/${invoice.id}/edit`);
            }
          };

          return (
            <WSList gap="XL">
              {!invoice.events?.markedPaidAt && (
                <>
                  <AlertEnableTaxWithholdings />
                </>
              )}

              <Card>
                <WSText.Heading4 mb="M">{payerName}</WSText.Heading4>
                {invoice.events?.refundedAt ? (
                  <InvoiceDetailsHeader
                    title="You got paid"
                    description={`${
                      (invoice.amountDetails?.refunds ?? 0) >=
                      (invoice.amount ?? 0)
                        ? "Full refund"
                        : "Partial refund"
                    } • ${toWSDateString(
                      invoice.events.refundedAt,
                      "monthDate"
                    )}`}
                    status="Deposited"
                    buttonProps={{
                      children: "Download receipt PDF",
                      onClick: () => {
                        downloadInvoiceReceiptPdf({
                          invoiceId: invoice.id!
                        });
                      },
                      loading: downloadInvoiceReceiptPdfMeta.isLoading
                    }}
                    invoice={invoice}
                  />
                ) : invoice.events?.depositedAt ||
                  invoice.events?.instantPayoutAt ? (
                  <InvoiceDetailsHeader
                    title="You got paid"
                    description={getInvoicePayoutSummaryV3(invoice)}
                    status="Deposited"
                    buttonProps={{
                      children: "Download receipt PDF",
                      onClick: () => {
                        downloadInvoiceReceiptPdf({
                          invoiceId: invoice.id!
                        });
                      },
                      loading: downloadInvoiceReceiptPdfMeta.isLoading
                    }}
                    invoice={invoice}
                  />
                ) : invoice.events?.paidAt ? (
                  <InvoiceDetailsHeader
                    title={
                      selectorIsInvoicePaidOffPlatform(invoice)
                        ? "Invoice was paid outside of Wingspan"
                        : "Your money is on its way!"
                    }
                    description={`Paid ${toWSDateString(
                      invoice.events.paidAt,
                      "monthDayYear"
                    )}`}
                    status="Paid"
                    buttonProps={
                      getIsInstantPayoutAllowedV3(
                        invoice,
                        featureFlags.internationalIP
                      )
                        ? {
                            children: "Request instant payout",
                            onClick: () => {
                              history.push(
                                `${location.pathname}/instant-deposit`
                              );
                            }
                          }
                        : {
                            children: "Download receipt PDF",
                            onClick: () => {
                              downloadInvoiceReceiptPdf({
                                invoiceId: invoice.id!
                              });
                            },
                            loading: downloadInvoiceReceiptPdfMeta.isLoading
                          }
                    }
                    invoice={invoice}
                  />
                ) : invoice.status === InvoiceStatus.PAYMENT_IN_TRANSIT ? (
                  <InvoiceDetailsHeader
                    title="Waiting on client's wire transfer to arrive"
                    description="Your money is on its way!"
                    status="Paid"
                    buttonProps={{
                      children: "Copy invoice link",
                      onClick: copyInvoiceLink,
                      loading: createInvoiceSecureLinkMeta.isLoading
                    }}
                    invoice={invoice}
                  />
                ) : invoice.events?.emailViewedAt &&
                  invoice.events.emailViewedAt.length > 0 &&
                  invoice.status === InvoiceStatus.OVERDUE ? (
                  <InvoiceDetailsHeader
                    title="Invoice payment is overdue"
                    description="We recommend personally following up"
                    status="Viewed"
                    buttonProps={{
                      children: "Email client",
                      onClick: () => {
                        sendInvoiceReminder({ invoiceId: invoice.id! });
                      },
                      loading: sendInvoiceReminderMeta.isLoading
                    }}
                    icon="info-circle"
                    iconColor="red400"
                    invoice={invoice}
                  />
                ) : invoice.events?.emailViewedAt &&
                  invoice.events.emailViewedAt.length > 0 ? (
                  <InvoiceDetailsHeader
                    title="Your invoice has been viewed"
                    description={`Seen by ${recipient}`}
                    status="Viewed"
                    buttonProps={{
                      children: "Copy invoice link",
                      onClick: copyInvoiceLink,
                      loading: createInvoiceSecureLinkMeta.isLoading
                    }}
                    invoice={invoice}
                  />
                ) : invoice.status === InvoiceStatus.OVERDUE ? (
                  <InvoiceDetailsHeader
                    title="Invoice payment is overdue"
                    description="We recommend personally following up"
                    status="Sent"
                    buttonProps={{
                      children: "Email client",
                      onClick: () => {
                        sendInvoiceReminder({ invoiceId: invoice.id! });
                      },
                      loading: sendInvoiceReminderMeta.isLoading
                    }}
                    icon="info-circle"
                    iconColor="red400"
                    invoice={invoice}
                  />
                ) : invoice.status === InvoiceStatus.OPENED ? (
                  <InvoiceDetailsHeader
                    title="Your invoice has been sent"
                    description={`Sent to ${recipient}`}
                    status="Sent"
                    buttonProps={{
                      children: "Copy invoice link",
                      onClick: copyInvoiceLink,
                      loading: createInvoiceSecureLinkMeta.isLoading
                    }}
                    invoice={invoice}
                  />
                ) : invoice.status === InvoiceStatus.PENDING ? (
                  <InvoiceDetailsHeader
                    title="This is pending"
                    description={
                      <>
                        <WSText>Will be sent to {recipient}</WSText>

                        <WSButton.Link my="M" onClick={intercomService.open}>
                          Ask about pending invoices
                        </WSButton.Link>
                      </>
                    }
                    buttonProps={editButtonProps}
                    invoice={invoice}
                  />
                ) : (
                  <InvoiceDetailsHeader
                    title="This is a draft"
                    description="Send now or schedule for later"
                    buttonProps={
                      (invoice.amount ?? 0) > 0
                        ? {
                            children: "Send now",
                            onClick: () => {
                              sendInvoice({
                                invoiceId
                              });
                            },
                            loading: sendInvoiceMeta.isLoading
                          }
                        : editButtonProps
                    }
                    invoice={invoice}
                  />
                )}
              </Card>

              {invoice.recurringInvoiceId && (
                <InvoiceRecurringSeriesCard
                  recurringInvoiceId={invoice.recurringInvoiceId}
                />
              )}

              <InvoiceDetails
                invoice={invoice}
                payer={selectorPayerResponseFromV3(payer)}
                payerName={payerName}
                is1099Flow={is1099Flow}
              />

              {featureFlags.invoicePaymentsDetails &&
                invoice.payments &&
                invoice.payments.length > 0 && (
                  <Card>
                    <WSText.Heading5 mb="XL">Payments</WSText.Heading5>
                    <InvoicePayments invoice={invoice} />
                  </Card>
                )}

              {featureFlags.refundsPane &&
                invoice.refunds &&
                invoice.refunds.length > 0 && (
                  <Card>
                    <WSText.Heading5 mb="XL">Refunds</WSText.Heading5>
                    <InvoiceRefunds invoice={invoice} />
                  </Card>
                )}

              <InvoiceActivity
                invoice={invoice}
                payer={selectorPayerResponseFromV3(payer)}
              />

              <QuickbooksWrapper>
                <QuickbooksHistory entityId={invoice.id!} Wrapper={Card} />
              </QuickbooksWrapper>
            </WSList>
          );
        }}
      </WSQueries>
    </DetailsLayout>
  );
};
